import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import { TFunction } from 'i18next';
import _ from 'lodash';
import { $enum } from 'ts-enum-util';
import * as Yup from 'yup';

import { CountryPhone } from '../../../../../enums/countries';
import { EmailAction, EmailRecipient } from '../../../../../enums/email';
import { Address } from '../../../../../types/address';
import { ClientResource } from '../../../../../types/api/clients';
import { AddressInitialValues } from '../../../../../utils/constants/address';
import {
  getNotificationChannel,
  getNotificationRecipient,
  getNotificationType,
} from '../../../../../utils/constants/entities/notifications';
import {
  bankAccountRegex,
  domainNameRegex,
  uintRegex,
} from '../../../../../utils/constants/regex';
import { invalidTabHeaderIcon } from '../../../../../utils/globals';
import { getChangedValues } from '../../../../../utils/helpers/object';
import {
  tryFloat,
  tryInt,
  tryString,
  tryTimeRange,
} from '../../../../../utils/helpers/parse';
import {
  formatMobileNumber,
  formatPhoneNumber,
  getPhoneorMobileNumberRegion,
  isValidMobileNumber,
  isValidPhoneNumber,
} from '../../../../../utils/helpers/phoneNumbers';
import { isEmail } from '../../../../../utils/helpers/validation';
import {
  yupMaxField,
  yupMinField,
  yupRequiredField,
} from '../../../../../utils/helpers/yup';
import { getAddressSchema } from '../../../../validations/address';

export type ImportListTypesData = {
  changeable_payment: number;
  class_name: string;
  id: number;
  isprakac_cenovnik_id: number;
  klient_isSender: number;
  name: string;
  naracatel_cenovnik_id: number;
  plaka_osiguruvanje_isprakac: number;
  plaka_otkup_isprakac: number;
  plaka_postarina_isprakac: number;
  plaka_povraten_dokument_isprakac: number;
  primac_cenovnik_id: number;
};

export type Headquarters = {
  id: number;
  korisnik_id: number;
  direkcija_id: number;
  pravno_lice: number;
  faktura: number;
  ime: string;
  email: string;
  telefon: string;
  mobilen: string;
  address_id: number;
  country_id: number;
  country_code: string;
  street_id: number;
  street_name: string;
  place_id: number;
  place_name: string;
  municipality_id: number;
  municipality_name: string;
  city_id: number;
  city_name: string;
  postcode: string;
  housenumber: string;
  entrance: string;
  apartment: string;
  raw_name: string;
  same_price_list_for_subsidiaries: any; // for now any
};

type GeneralInfoTab = {
  id?: number | null;
  ime: string;
  email?: string;
  address: Address;
  web: string;
  komentar: string;
  dejnost_id: number | null;
  pravno_lice: boolean;
  interen_klient: boolean;
  faks: string;
  faks_region?: string;
  mobilen: string;
  mobilen_region?: string;
  telefon: string;
  telefon_region?: string;

  // - Legal entity
  odgovorno_lice?: string;
  kontakt_lice?: string;
  referenca?: string;
};

type LegalDataTab = {
  direkcija_id?: number;
  direkcija_ime?: string;
  smetka?: string;
  danocen_broj?: string;
  maticen_broj?: string;
  working_hours_from?: any;
  working_hours_to?: any;
  broj_dogovor?: string;
  rok_plakanje?: string;
  cenovnik_id: any | null;
  rabotno_vreme: string;
  cod_bank_account_payment?: boolean;
  faktura?: boolean;
  cod_email_warrant: boolean;
  invoice_email_warrant: boolean;
  recipient_pricelist_id?: number | null;
  cod_department_id?: number;
  invoice_department_id?: number;
  same_price_list_for_subsidiaries?: boolean;
};

type DiscountsTab = {
  popust?: {
    Popust: string;
    ProizvodId: number;
    StatusId: number;
    UslugaId: number;
  }[];
};

type ImportListType = {
  id: any;
  name: string;
};

type OrdersTab = {
  allow_incomplete_address: boolean;
  orders_on_map_access: boolean;
  proof_of_delivery: boolean;
  import_list_types: ImportListType[];
};

export type NotificationDefault = {
  actor: number;
  channel: number;
  event: number;
  id: number;
  message: string;
  placeholders: string[];
  title?: string;
};

type NotificationTab = {
  sms_gateway: string;
  import_notification?: boolean | any;
  cod_email_warrant?: boolean;
  notifications?: { [key: string]: any };
};

type EmailConfigurationTab = { email_configuration: any }; // for now any !!!

export type ChargingDepartments = {
  id: any;
  name: string;
  headquarters_id: number;
  charge_customer_id: number;
};

type ChargingDepartmentsTab = {
  departments?: ChargingDepartments[];
};

export type FormValues = GeneralInfoTab &
  LegalDataTab &
  DiscountsTab &
  OrdersTab &
  NotificationTab &
  EmailConfigurationTab &
  ChargingDepartmentsTab & {
    recipient_pricelist_id?: number | null;
    [key: string]: any;
  };

const generalInfoTabFields = {
  id: null,
  ime: '',
  email: '',
  mobilen: '',
  mobilen_region: '',
  telefon: '',
  telefon_region: '',
  faks: '',
  web: '',
  komentar: '',
  dejnost_id: null,
  pravno_lice: false,
  interen_klient: false,
  address: AddressInitialValues,

  // - Legal entity
  odgovorno_lice: '',
  kontakt_lice: '',
  referenca: '',
};

function getLegalDataTabFields(isEditDialog: boolean) {
  return {
    direkcija_id: 0,
    direkcija_ime: '',
    maticen_broj: '',
    danocen_broj: '',
    smetka: '',
    broj_dogovor: '',
    rok_plakanje: '',
    cenovnik_id: null,
    rabotno_vreme: '',
    cod_bank_account_payment: false,
    faktura: false,
    cod_email_warrant: false,
    invoice_email_warrant: false,
    invoice_date_basis_type_id: null,
    working_hours_from: dayjs().hour(8).minute(0).toDate(),
    working_hours_to: dayjs().hour(16).minute(0).toDate(),
    recipient_pricelist_id: null,
    same_price_list_for_subsidiaries: false,
    ...(!isEditDialog
      ? { cod_department_id: -1, invoice_department_id: -1 }
      : {}),
  };
}

const discountsTabFields = {
  popust: [],
};

const ordersTabFields = {
  allow_incomplete_address: false,
  orders_on_map_access: false,
  proof_of_delivery: false,
  import_list_types: [] as ImportListType[],
};

const chargingDepartmentsTabFields = {
  departments: [] as ChargingDepartments[],
};

const emailConfigurationTabFields = {
  email_configuration: Object.fromEntries(
    $enum(EmailAction)
      .getValues()
      .map((emailAction) => [
        emailAction,
        Object.fromEntries(
          $enum(EmailRecipient)
            .getValues()
            .map((emailRecipient) => [emailRecipient, []])
        ),
      ])
  ),
};

function getNotificationsTabFields(t: TFunction) {
  return {
    sms_gateway: '',
    import_notification: false,
    notification: generateNotificationFields(t),
  };
}

function getFields(t: TFunction, isEditDialog: boolean) {
  const legalDataTabFields = getLegalDataTabFields(isEditDialog);
  const notificationsTabFields = getNotificationsTabFields(t);

  return {
    ...generalInfoTabFields,
    ...legalDataTabFields,
    ...discountsTabFields,
    ...notificationsTabFields,
    ...ordersTabFields,
    ...chargingDepartmentsTabFields,
    ...emailConfigurationTabFields,
  };
}

// prettier-ignore
export const ckEditorConfig = {
  toolbar: [
    'heading', '|',
    'bold', 'italic', '|',
    'link', '|',
    'bulletedList', 'numberedList', '|',
    'blockQuote', '|',
    'undo', 'redo'
  ]
}

type TabName =
  | 'general-info'
  | 'legal-data'
  | 'discounts'
  | 'orders'
  | 'notifications'
  | 'charging-departments'
  | 'email-configuration';

function getHasTabErrors(t: TFunction, isEditDialog: boolean) {
  const legalDataTabFields = getLegalDataTabFields(isEditDialog);
  const notificationsTabFields = getNotificationsTabFields(t);

  return function hasTabErrors(
    tabName: TabName,
    values: FormValues,
    errors: any
  ) {
    const errorsKeys = Object.keys(errors);

    const tabNameFieldsMap = {
      'general-info': {
        ..._.omit(generalInfoTabFields, [
          'odgovorno_lice',
          'kontakt_lice',
          'referenca',
          'cenovnik_id',
          'rabotno_vreme',
        ]),
      },
      'legal-data': !values.pravno_lice
        ? {}
        : { ...legalDataTabFields, working_hours_from: true },
      discounts: discountsTabFields,
      orders: ordersTabFields,
      notifications: notificationsTabFields,
      'charging-departments': chargingDepartmentsTabFields,
      'email-configuration': emailConfigurationTabFields,
    };

    const tabFields = Object.keys(tabNameFieldsMap[tabName]);

    for (const field of tabFields) {
      if (errorsKeys.includes(field)) {
        return true;
      }
    }

    return false;
  };
}

export function generateNotificationFields(t: TFunction) {
  const notificationType = getNotificationType(t);
  const notificationChannel = getNotificationChannel(t);
  const notificationRecipient = getNotificationRecipient(t);

  type NotificationFieldValue = {
    isChecked: boolean;
    title?: string | null;
    message?: string | null;
  };

  const notificationFields: Record<string, NotificationFieldValue> = {};

  for (let nType of notificationType) {
    for (let nrType of notificationRecipient) {
      for (let nChannel of notificationChannel) {
        let obj: any = {
          isChecked: false,
        };

        switch (nChannel.id) {
          case 1:
            obj.title = null;
            obj.message = null;
            break;

          case 2:
            obj.message = null;
            break;

          default:
        }

        notificationFields[`${nType.id}-${nrType.id}-${nChannel.id}`] = obj;
      }
    }
  }

  return notificationFields;
}

export function generateDiscountObject(discountData: any) {
  let discount = [];

  if (typeof discountData === 'string') {
    try {
      const parsedDiscount = JSON.parse(discountData);

      if (typeof parsedDiscount === 'string') {
        const finalPass = JSON.parse(parsedDiscount);

        discount = Array.isArray(finalPass) ? finalPass : [];
      } else {
        discount = parsedDiscount;
      }
    } catch (e) {
      Sentry.captureException(e, {
        extra: {
          data: discountData,
        },
      });
    }
  } else if (Array.isArray(discountData)) {
    discount = discountData;
  }

  // Filter and protect from invalid values
  return discount
    .filter((d: any) => {
      const percent = tryInt(d.Popust);
      const serviceId = tryInt(d.UslugaId);

      return (
        percent !== undefined &&
        serviceId !== undefined &&
        percent >= 0 &&
        percent <= 100
      );
    })
    .map((d: any) => ({
      ...d,
      StatusId: tryInt(d.StatusId) ?? null,
      Popust: tryFloat(d.Popust) ?? '',
    }));
}

export function tabIcon(
  t: TFunction,
  tabName: TabName,
  values: FormValues,
  errors: any,
  isEditDialog: boolean
) {
  const hasTabErrors = getHasTabErrors(t, isEditDialog);
  return hasTabErrors(tabName, values, errors) ? invalidTabHeaderIcon : {};
}

export function generateNotificationsObject(
  t: TFunction,
  data: ClientResource | any
) {
  let notifications = generateNotificationFields(t);

  if (!data?.notification) {
    return notifications;
  }

  for (let n of data.notification) {
    if (!n.event || !n.actor || !n.channel) {
      continue;
    }

    const id = `${n.event}-${n.actor}-${n.channel}`;
    let newObj: NotificationDefault = {} as NotificationDefault;

    if (n.channel === 1) {
      newObj.title = n.title;
      newObj.message = n.message;
    } else if (n.channel === 2) {
      newObj.message = n.message;
    }

    notifications[id] = {
      isChecked: true,
      ...newObj,
    };
  }
  return notifications;
}

function initialEmailConfiguration(emailConfiguration?: any) {
  return _.isObject(emailConfiguration)
    ? _.merge(
        {},
        emailConfigurationTabFields.email_configuration,
        emailConfiguration
      )
    : emailConfigurationTabFields.email_configuration;
}

export function getDefaultValues(
  t: TFunction,
  isEditDialog: boolean | undefined,
  clientData: ClientResource | undefined,
  configsData: any
  // chargingDepartmentsData: ChargingDepartments[] | undefined
) {
  const fields = getFields(t, isEditDialog!);
  const fieldSettings = customerConfigsOnly(configsData?.data);
  const tempData = isEditDialog ? clientData : ({} as ClientResource);
  let initialValues: any = { ...fields };

  const pricelistId =
    tryInt(clientData?.cenovnik_id) ??
    fieldSettings.DefaultPricelistId ??
    fields.cenovnik_id;

  if (pricelistId !== undefined) {
    initialValues.cenovnik_id = pricelistId;
  }

  if (clientData && Object.keys(clientData).length) {
    initialValues.pravno_lice = !!clientData.pravno_lice;

    const occupationId: any = tryInt(clientData.dejnost_id);
    if (occupationId !== undefined) {
      initialValues.dejnost_id = occupationId;
    }

    initialValues.id = clientData.id ?? fields.id;
    initialValues.ime = clientData.ime ?? fields.ime;
    initialValues.email = clientData.email ?? fields.email;

    initialValues.address = clientData?.address ?? fields.address;

    initialValues.web = tryString(clientData.web) ?? fields.web;

    initialValues.odgovorno_lice =
      tryString(clientData.odgovorno_lice) ?? fields.odgovorno_lice;

    initialValues.kontakt_lice =
      tryString(clientData.kontakt_lice) ?? fields.kontakt_lice;

    initialValues.referenca =
      tryString(clientData.referenca) ?? fields.referenca;
    initialValues.komentar = tryString(clientData.komentar) ?? fields.komentar;

    initialValues.broj_dogovor =
      tryString(clientData.broj_dogovor) ?? fields.broj_dogovor;

    initialValues.maticen_broj =
      tryString(clientData.maticen_broj) ?? fields.maticen_broj;

    initialValues.danocen_broj =
      tryString(clientData.danocen_broj) ?? fields.danocen_broj;

    initialValues.smetka = tryString(clientData.smetka) ?? fields.smetka;

    initialValues.rok_plakanje =
      tryString(clientData.rok_plakanje) ?? fields.rok_plakanje;

    initialValues.sms_gateway =
      tryString(clientData.sms_gateway) ?? fields.sms_gateway;

    initialValues.interen_klient = !!clientData.interen_klient;
    initialValues.faktura = !!clientData.faktura;
    initialValues.same_price_list_for_subsidiaries =
      clientData.same_price_list_for_subsidiaries === 1;

    if (isEditDialog) {
      initialValues.cod_department_id =
        clientData.cod_department_id ?? fields.cod_department_id;
      initialValues.invoice_department_id =
        clientData.invoice_department_id ?? fields.invoice_department_id;
    }

    initialValues.cod_bank_account_payment =
      !!clientData.cod_bank_account_payment;

    initialValues.allow_incomplete_address =
      !!clientData.allow_incomplete_address;

    initialValues.proof_of_delivery = !!clientData.proof_of_delivery;
    initialValues.orders_on_map_access = !!clientData.orders_on_map_access;
    initialValues.import_notification = !!clientData.import_notification;
    initialValues.cod_email_warrant = !!clientData.cod_email_warrant;
    initialValues.invoice_email_warrant = !!clientData.invoice_email_warrant;
    initialValues.invoice_date_basis_type_id =
      clientData.invoice_date_basis_type_id ??
      fields.invoice_date_basis_type_id;

    initialValues.import_list_types =
      clientData?.import_list_types && clientData?.import_list_types?.length > 0
        ? clientData.import_list_types
        : fields.import_list_types;

    const workingHours =
      tryTimeRange(clientData.rabotno_vreme, true) ?? fields.rabotno_vreme;

    if (Array.isArray(workingHours)) {
      initialValues.working_hours_from = workingHours[0];
      initialValues.working_hours_to = workingHours[1];
    } else {
      initialValues.rabotno_vreme = workingHours;
    }

    initialValues.email_configuration = initialEmailConfiguration(
      tempData!.email_configuration
    );
  } else {
    initialValues.email_configuration = initialEmailConfiguration();
  }

  initialValues.recipient_pricelist_id =
    clientData?.recipient_pricelist_id ?? fields.recipient_pricelist_id;

  initialValues.notification = generateNotificationsObject(t, clientData);

  initialValues.popust = generateDiscountObject(clientData?.popust);

  if (clientData?.id === clientData?.direkcija_id && isEditDialog) {
    initialValues.departments = clientData?.departments ?? [];
  }

  initialValues.telefon = tryString(clientData?.telefon) ?? fields.telefon;
  initialValues.telefon_region = getPhoneorMobileNumberRegion(
    initialValues.telefon
  );

  initialValues.mobilen = tryString(clientData?.mobilen) ?? fields.mobilen;
  initialValues.mobilen_region = getPhoneorMobileNumberRegion(
    initialValues.mobilen
  );

  initialValues.direkcija_id = clientData?.direkcija_id ?? fields.direkcija_id;
  initialValues.direkcija_ime = clientData?.direkcija_id
    ? clientData.direkcija_id && clientData.direkcija_id === clientData.id
      ? t('This client is a headquarter')
      : clientData.direkcija_id && clientData.direkcija_id !== clientData.id
      ? clientData.direkcija_ime
      : t('This client is a headquarter')
    : initialValues.direkcija_ime;

  // initialValues.faks_region = getPhoneorMobileNumberRegion(initialValues.faks);

  return initialValues;
}

function toApiData(values: FormValues, clientId: number | null = null) {
  let apiData = {
    ...values,
  };
  // Number values
  if (!apiData.id) {
    delete apiData.id;
  }

  apiData.dejnost_id = apiData.dejnost_id; // parseInt

  apiData.cenovnik_id = parseInt(apiData.cenovnik_id);
  (apiData as any).same_price_list_for_subsidiaries =
    apiData.same_price_list_for_subsidiaries ? 1 : 0;

  if (apiData.direkcija_id !== null) {
    apiData.direkcija_id = apiData.direkcija_id; // parseInt
  }

  if (apiData.recipient_pricelist_id !== null) {
    apiData.recipient_pricelist_id = apiData.recipient_pricelist_id;
  } else {
    delete apiData.recipient_pricelist_id;
  }

  if (apiData.rok_plakanje && apiData.rok_plakanje.length > 0) {
    apiData.rok_plakanje = apiData.rok_plakanje; // parseInt
  }

  // Working hours
  if (apiData.working_hours_from && apiData.working_hours_to) {
    const start = dayjs(apiData.working_hours_from).format('HH:mm');
    const end = dayjs(apiData.working_hours_to).format('HH:mm');
    apiData.rabotno_vreme = `${start}-${end}`;
  }

  // List Types
  apiData.import_list_types = _.map(apiData.import_list_types, 'id') as any; // as any becouse array of number not objects for API

  // Notification
  let notification = [];

  for (let notificationObj in apiData.notification) {
    if (!apiData.notification[notificationObj].isChecked) {
      continue;
    }

    const [nType, nrType, nChannel] = notificationObj.split('-');

    notification.push({
      event: nType,
      actor: nrType,
      channel: nChannel,
      ...(parseInt(nChannel) === 1
        ? {
            title: apiData.notification[notificationObj].title,
            message: apiData.notification[notificationObj].message,
          }
        : {
            message: apiData.notification[notificationObj].message,
          }),
    });
  }

  apiData.departments = apiData.departments?.map((department: any) => ({
    id: department.id ? Number(department.id) : null,
    name: department.name,
    headquarters_id: department.headquarters_id
      ? Number(department.headquarters_id)
      : 0,
    charge_customer_id: department.charge_customer_id
      ? Number(department.charge_customer_id)
      : 0,
  }));

  // apiData.cod_email_warrant = tryInt(apiData.cod_email_warrant) ?? null;
  // apiData.invoice_email_warrant = tryInt(apiData.invoice_email_warrant) ?? null;
  // apiData.import_notification = tryInt(apiData.import_notification) ?? null;

  apiData.cod_email_warrant = apiData.cod_email_warrant ?? null;
  apiData.invoice_email_warrant = apiData.invoice_email_warrant ?? null;
  apiData.import_notification = apiData.import_notification ?? null;

  apiData.notification = notification;

  // Delete conditional fields
  if (!apiData.smetka) {
    delete apiData.cod_bank_account_payment;
  }

  if (!apiData.pravno_lice) {
    delete apiData.kontakt_lice;
    delete apiData.odgovorno_lice;
    delete apiData.referenca;
    delete apiData.broj_dogovor;
    delete apiData.danocen_broj;

    delete apiData.direkcija_id;
    delete apiData.rok_plakanje;
    delete apiData.popust;
    delete apiData.faktura;
    delete apiData.same_price_list_for_subsidiaries;

    delete apiData.cod_department_id;
    delete apiData.invoice_department_id;
    delete apiData.departments;

    delete apiData.import_notification;
  } else {
    if (!apiData.broj_dogovor) {
      delete apiData.faktura;
    }
  }

  if (!apiData.faktura) {
    delete apiData.rok_plakanje;
    delete apiData.popust;
    apiData.invoice_date_basis_type_id = null;
  }

  if (clientId && apiData.direkcija_id !== clientId) {
    // if there is clientId action is edit and if client is not headquarter then remove departments
    delete apiData.departments;
  } else if (!clientId && apiData.direkcija_id !== 0) {
    // if there is not clientId action is create and if client is not headquarter then remove departments
    delete apiData.departments;
  }

  if (apiData.direkcija_id === 0 || apiData.direkcija_id === null) {
    // only on create new client, if this client is headquarter we set 0 value, so if is 0 delete from apiData
    // on edit we set direkcija_id -> client_id
    delete apiData.direkcija_id;
  }
  // }

  // Mobile and phone numbers
  if (apiData.telefon) {
    apiData.telefon = formatPhoneNumber(
      apiData.telefon,
      apiData.telefon_region as CountryPhone
    );
  }

  if (apiData.mobilen) {
    apiData.mobilen = formatMobileNumber(
      apiData.mobilen,
      apiData.mobilen_region as CountryPhone
    );
  }

  // Delete unnecessary data
  delete apiData.working_hours_from;
  delete apiData.working_hours_to;
  delete apiData.telefon_region;
  delete apiData.mobilen_region;
  delete apiData.faks_region;
  delete apiData.direkcija_ime;

  // If create dialog
  if (clientId === null) {
    Object.keys(apiData).forEach((key) => {
      if (typeof apiData[key] === 'string' && apiData[key].trim() === '') {
        delete apiData[key];
      }
    });
  }

  // Settings dependent fields
  if (!apiData.email) {
    delete apiData.email;
  }

  if (!apiData.broj) {
    // check for this one
    delete apiData.broj;
  }

  if (!apiData.smetka) {
    delete apiData.smetka;
  }

  if (!apiData.then) {
    // check for this one
    delete apiData.then;
  }

  if (!apiData.maticen_broj) {
    delete apiData.maticen_broj;
  }

  if (apiData.popust) {
    apiData.popust = generateDiscountObject(apiData.popust);
  }

  return apiData;
}

export function createDialogApiData(values: FormValues) {
  const apiData = toApiData(values);

  return _.pick(
    apiData,
    Object.keys(apiData).filter((k) => {
      const val = apiData[k];

      if (typeof val === 'number' || typeof val === 'boolean') {
        return true;
      }

      if (Array.isArray(val)) {
        return val.length > 0;
      }

      if (_.isObject(val)) {
        return Object.keys(val).length > 0;
      }

      return !!k;
    })
  );
}

export function editDialogApiData(
  values: FormValues,
  clientId: string | null,
  initialValues: FormValues
) {
  const id = clientId ? parseInt(clientId ?? '') : null;
  const apiData = toApiData(values, id);
  const initialApiData = toApiData(initialValues, id);

  return getChangedValues(apiData, initialApiData);
}

function settingsDependentRequiredField(
  field: any,
  isRequired: any,
  requiredMessage: any
) {
  return isRequired ? field.required(requiredMessage) : field;
}

export function customerConfigsOnly(configsData: any) {
  return Object.fromEntries(
    (configsData ?? [])
      .filter((c: any) => c.section === 'Customer')
      .map((c: any) => [c.ident, c.value])
  );
}

export function isHeadquarter(
  pravno_lice: boolean,
  clientData: ClientResource | undefined,
  direkcija_id?: number
) {
  return (
    pravno_lice && (clientData?.id === direkcija_id || direkcija_id === 0) // 0 we set when we set this client is legal and This client is headquarter
  );
}

export function generateInvoicingCheckboxProperties(
  values: { direkcija_id?: number; broj_dogovor?: string; faktura?: boolean },
  isHeadquarter: boolean,
  headquarterData: ClientResource | undefined,
  clientData: ClientResource | undefined,
  t: TFunction
) {
  if (isHeadquarter) {
    if (values.broj_dogovor) {
      return { checked: values.faktura };
    } else {
      return {
        disabled: true,
        checked: false,
        tooltip: t('You have to specify a contract number first'),
      };
    }
  } else {
    if (!headquarterData) {
      return { disabled: true, checked: false, tooltip: t('Loading...') };
    }

    if (values.direkcija_id !== 0 || values.direkcija_id !== clientData?.id) {
      return {
        disabled: true,
        checked: Boolean(headquarterData.faktura),
        tooltip: t('Inherited value from headquarter'),
      };
    }

    if (!headquarterData.broj_dogovor) {
      return {
        disabled: true,
        checked: false,
        tooltip: t(
          "The headquarter of this client doesn't have a contract number specified"
        ),
      };
    } else {
      return { checked: values.faktura };
    }
  }
}

export function getValidationSchema(
  t: TFunction,
  configs: any,
  // clientData: ClientResource | undefined,
  isEditDialog: boolean | undefined
) {
  const fieldSettings = customerConfigsOnly(configs?.data);

  return Yup.object().shape({
    id: Yup.number().nullable().optional(),
    ime: Yup.string()
      .required(yupRequiredField(t, t('Name')))
      .min(2, ({ min }) => yupMinField(t, t('Name'), min))
      .max(210, ({ max }) => yupMaxField(t, t('Name'), max)),
    email: settingsDependentRequiredField(
      Yup.string().email().max(254),
      fieldSettings.RequiredEmail,
      yupRequiredField(t, t('Email'))
    ),
    address: getAddressSchema(t, fieldSettings),
    // web: Yup.string()
    //   .matches(domainNameRegex, t('Please enter a valid domain name or URL'))
    //   .max(255, ({ max }) => yupMaxField(t, 'Web', max)),
    web: Yup.string()
      .test(
        'web-optional-validation',
        t('Please enter a valid domain name or URL'),
        function (value) {
          if (!value || value.trim().length === 0) return true; // if empty, no need validation
          return domainNameRegex.test(value);
        }
      )
      .max(255, ({ max }) => yupMaxField(t, 'Web', max)),
    komentar: Yup.string().max(500, ({ max }) =>
      yupMaxField(t, t('Comment'), max)
    ),
    dejnost_id: Yup.number()
      .max(500, ({ max }) => yupMaxField(t, t('Dejnost'), max))
      .nullable(),
    pravno_lice: Yup.boolean(),
    interen_klient: Yup.boolean(),
    faks: Yup.string().test(
      'telefon',
      t('Invalid phone number'),
      (value, context) => {
        if (!value) {
          return true;
        }

        const { faks_region } = context.parent;
        return isValidPhoneNumber(value, faks_region);
      }
    ),
    telefon: Yup.string()
      .test('telefon', t('Invalid phone number'), (value, context) => {
        if (!value) {
          return true;
        }

        const { telefon_region } = context.parent;
        return isValidPhoneNumber(value, telefon_region);
      })
      .test(
        'telefon',
        t('At least phone or mobile must be filled out'),
        function (value) {
          if (!fieldSettings.RequiredMobileOrPhone) {
            return true;
          }

          const { mobilen } = this.parent;

          if (!mobilen) {
            return value != null;
          }

          return true;
        }
      ),
    mobilen: Yup.string()
      .test('mobilen', t('Invalid mobile phone number'), (value, context) => {
        if (!value) {
          return true;
        }

        const { mobilen_region } = context.parent;
        return isValidMobileNumber(value, mobilen_region);
      })
      .test(
        'mobilen',
        t('At least phone or mobile must be filled out'),
        function (value) {
          if (!fieldSettings.RequiredMobileOrPhone) {
            return true;
          }

          const { telefon } = this.parent;

          if (!telefon) {
            return value != null;
          }

          return true;
        }
      ),
    // - Legal entity
    odgovorno_lice: Yup.mixed().when('pravno_lice', {
      is: true,
      then: Yup.string().max(45),
    }),
    kontakt_lice: Yup.mixed().when('pravno_lice', {
      is: true,
      then: Yup.string().max(45),
    }),
    referenca: Yup.mixed().when('pravno_lice', {
      is: true,
      then: Yup.string().max(45),
    }),
    // LegalDataTab
    direkcija_id: Yup.number().nullable(),
    direkcija_ime: Yup.string().nullable().optional(),
    smetka: settingsDependentRequiredField(
      Yup.string().test(
        'bank-account-optional-validation',
        t('Invalid bank account number'),
        function (value) {
          if (!value || value.trim().length === 0) return true; // if empty, no need validation
          return bankAccountRegex.test(value);
        }
      ),
      fieldSettings.RequiredBankAccount,
      yupRequiredField(t, t('Bank account'))
    ),
    // smetka: settingsDependentRequiredField(
    //   Yup.string().matches(bankAccountRegex, t('Invalid bank account number')),
    //   fieldSettings.RequiredBankAccount,
    //   yupRequiredField(t, t('Bank account'))
    // ),
    danocen_broj: Yup.string()
      .test(
        'danocen_broj',
        yupRequiredField(t, t('Tax No.')),
        (value, context) => {
          const { pravno_lice } = context.parent;
          if (!pravno_lice) {
            return true;
          }
          // if (
          //   (!isEditDialog && direkcija_id === 0) ||
          //   (isEditDialog && direkcija_id === clientData?.id)
          // ) {
          //   return true;
          // } else {
          if (value) {
            return true;
          } else {
            return false;
          }
          // }
        }
      )
      .test(
        'danocen_broj',
        t('Tax No. must be exactly 13 digits long'),
        (value) => {
          if (value) {
            return value.length === 13;
          }
          return true;
        }
      ),
    maticen_broj: Yup.string()
      .test(
        'maticen_broj',
        yupRequiredField(t, t('Social Security No.')),
        (value, context) => {
          const { pravno_lice } = context.parent;
          if (!pravno_lice) {
            return true;
          }
          // if (
          //   (!isEditDialog && direkcija_id === 0) ||
          //   (isEditDialog && direkcija_id === clientData?.id)
          // ) {
          //   return true;
          // } else {
          if (value) {
            return true;
          } else {
            return false;
          }
          // }
        }
      )
      .test(
        'maticen_broj',
        t('Social Security No. must be exactly 13 digits long'),
        (value, context) => {
          const { pravno_lice } = context.parent;

          if (value && !pravno_lice) {
            return value.length === 13;
          }
          return true;
        }
      )
      .test(
        'maticen_broj',
        t('Social Security No. must be exactly 7 digits long'),
        (value, context) => {
          const { pravno_lice } = context.parent;

          if (value && pravno_lice) {
            return value.length === 7;
          }
          return true;
        }
      ),
    working_hours_from: Yup.mixed().test(
      'working_hours_from',
      t('Invalid time range'),
      function () {
        const { working_hours_from, working_hours_to } = this.parent;

        // rabotno_vreme is an optional field
        if (!working_hours_from && !working_hours_to) {
          return true;
        }

        // If only one of them is empty, we have an error
        if (
          (!working_hours_from && working_hours_to) ||
          (working_hours_from && !working_hours_to)
        ) {
          return false;
        }

        // If both are filled out, validate from <= to
        const from = dayjs(working_hours_from);
        const to = dayjs(working_hours_to);

        if (from.isValid() && to.isValid()) {
          return to.isAfter(from);
        }

        return true;
      }
    ),
    working_hours_to: Yup.string().optional(),
    broj_dogovor: Yup.string().max(100, ({ max }) =>
      yupMaxField(t, t('Contract No.'), max)
    ),
    rok_plakanje: Yup.mixed().when('pravno_lice', {
      is: true,
      then: Yup.string().matches(
        uintRegex,
        t('Please enter a valid number of days')
      ),
    }),

    // Orders
    import_list_types: Yup.array().of(
      Yup.object({
        id: Yup.number().required(),
        name: Yup.string().required(),
      })
    ),

    // DiscountsTab
    popust: Yup.array().test(
      'popust',
      t('Invalid discount'),
      (discounts: any) => {
        for (let i = 0; i < discounts.length; i++) {
          const firstDiscount = discounts[i];
          const discount = tryFloat(firstDiscount.Popust);

          if (discount === undefined || discount < 0 || discount > 100) {
            return false;
          }

          for (let j = i + 1; j < discounts.length; j++) {
            const secondDiscount = discounts[j];

            if (
              (firstDiscount.UslugaId === secondDiscount.UslugaId ||
                firstDiscount.UslugaId === null ||
                secondDiscount.UslugaId === null) &&
              (firstDiscount.ProizvodId === secondDiscount.ProizvodId ||
                firstDiscount.ProizvodId === null ||
                secondDiscount.ProizvodId === null) &&
              (firstDiscount.StatusId === secondDiscount.StatusId ||
                firstDiscount.StatusId === null ||
                secondDiscount.StatusId === null)
            ) {
              return false;
            }
          }
        }

        return true;
      }
    ),

    // ChargingDepartmentsTab
    cod_department_id: Yup.string().when('pravno_lice', {
      is: (value: any) => value === true && isEditDialog,
      then: Yup.string().required(yupRequiredField(t, t('COD Department'))),
    }),
    invoice_department_id: Yup.string().when('pravno_lice', {
      is: (value: any) => value === true && isEditDialog,
      then: Yup.string().required(yupRequiredField(t, t('Invoice Department'))),
    }),
    departments: Yup.array().of(
      Yup.object({
        id: Yup.number().optional(),
        name: Yup.string().required(yupRequiredField(t, t('Department'))),
        headquarters_id: Yup.number().optional(),
        charge_customer_id: Yup.string().required(
          yupRequiredField(t, t('Client'))
        ),
      })
    ),

    // EmailConfigurationTab
    email_configuration: Yup.object().test(
      'email_configuration',
      t('Invalid email address(es) entered!'),
      (value) => {
        let emailAddresses = [];

        const emailActionEnumValues = $enum(EmailAction).getValues();
        const emailRecipientEnumValues = $enum(EmailRecipient).getValues();

        for (const action of emailActionEnumValues) {
          for (const recipient of emailRecipientEnumValues) {
            const emails = value[action]?.[recipient];
            if (Array.isArray(emails)) {
              emailAddresses.push(...(emails as any));
            }
          }
        }

        return emailAddresses.every(isEmail);
      }
    ),
  });
}

export function allowedErrors(t: TFunction) {
  return {
    ime: {
      already_in_use: t('A client with this name already exists'),
    },
    email: {
      already_in_use: t('A client with this email already exists'),
      duplicate: t('A client with this email already exists'),
    },
    mobilen: {
      duplicate: t('A client with this mobile number already exists'),
      already_in_use: t('A client with this mobile number already exists'),
    },
    telefon: {
      duplicate: t('A client with this phone number already exists'),
      already_in_use: t('A client with this phone number already exists'),
    },
    maticen_broj: {
      duplicate: t('A client with this ID already exists'),
      combination_ssn_tax_already_in_use: t(
        'A client with this SSN - Tax No. combination already exists'
      ),
    },
    danocen_broj: {
      duplicate: t('A client with this tax number already exists'),
      combination_ssn_tax_already_in_use: t(
        'A client with this SSN - Tax No. combination already exists'
      ),
    },
  };
}
